import { useEffect, useRef, useState } from 'react';
import { Send, Sparkles } from 'lucide-react';
import type { DeliveryMilestone, QuoteBlock } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';

// Scoping a SOW by talking.
//
// This is the way in. The block builder below it is for CORRECTING the
// result - fixing a number, cutting a bullet - not for producing a
// document from nothing, which is what it was being asked to do and was
// bad at. Nobody scopes an engagement by clicking "add bulleted list".

/**
 * What a session produces: the scope sections, the hours estimate by
 * area (each becomes an hours line), and the delivery plan. The rate,
 * the standard opening and the terms stay the agent's. A quote priced
 * by plans gets sections only.
 */
export interface SowDraft {
  sections: Array<{ heading: string; blocks: QuoteBlock[] }>;
  effort?: Array<{ area: string; hours: number }>;
  deliveryPlan?: DeliveryMilestone[];
}

/** What the quote says right now, as the chat is shown it each turn. */
export interface SowCurrent {
  sections: Array<{ heading: string; blocks: QuoteBlock[] }>;
  effort: Array<{ area: string; hours: number }>;
  deliveryPlan: Array<{ title: string; dueAfterDays: number; tasks: string[] }>;
  /** Block hours: no hours lines and no plan for the chat to write. */
  pricedByPlans: boolean;
}

interface Turn {
  role: 'user' | 'assistant';
  content: string;
  /** The draft produced on this turn, if any. */
  draft?: SowDraft | null;
}

interface Props {
  organizationName: string;
  quoteTitle: string;
  /**
   * What is in the quote right now. Sent with every turn so Claude
   * REVISES the document instead of redrafting it from the conversation
   * - which is what lets it say what changed, and stops it silently
   * dropping edits made by hand between turns.
   */
  currentDocument: SowCurrent | null;
  /**
   * Fills the quote with a new draft. Called automatically as soon as one
   * arrives: the whole point is that the SOW lands in the quote rather
   * than being copied across, so there is no apply step to forget.
   * It fills the boxes; saving is still the agent's.
   */
  onApply: (draft: SowDraft) => void;
  /** Text to start the message box with - a client's own request, when they asked from the portal. */
  initialInput?: string;
}

const inputClass =
  'block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2.5 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30';

const OPENERS = [
  'Two-host server refresh, VMware to Hyper-V, about 40 hours.',
  'Mailbox migration from on-prem Exchange to Microsoft 365.',
  'They keep getting user profile corruption and nobody knows why.',
];

function draftSummary(draft: SowDraft): string {
  const blocks = draft.sections.reduce((n, s) => n + s.blocks.length, 0);
  const sections = draft.sections.length;
  const parts = [
    sections + ' section' + (sections === 1 ? '' : 's'),
    blocks + ' block' + (blocks === 1 ? '' : 's'),
  ];
  if (draft.effort && draft.effort.length > 0) {
    const hours = draft.effort.reduce((n, e) => n + e.hours, 0);
    parts.push(hours + ' hours over ' + draft.effort.length + ' line' + (draft.effort.length === 1 ? '' : 's'));
  }
  if (draft.deliveryPlan && draft.deliveryPlan.length > 0) {
    const n = draft.deliveryPlan.length;
    parts.push(n + ' milestone' + (n === 1 ? '' : 's'));
  }
  return parts.join(' · ');
}

export function QuoteSowChat({
  organizationName,
  quoteTitle,
  currentDocument,
  onApply,
  initialInput,
}: Props): React.ReactElement {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState(initialInput ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns, busy]);

  async function send(text: string): Promise<void> {
    const message = text.trim();
    if (!message || busy) return;

    // The user's turn goes up immediately; the request carries the whole
    // conversation, because nothing about it is stored server-side.
    const next: Turn[] = [...turns, { role: 'user', content: message }];
    setTurns(next);
    setInput('');
    setBusy(true);
    setError(null);

    try {
      const data = await apiFetch<{ reply: string; draft: SowDraft | null }>(
        '/api/quote-documents/sow-chat',
        {
          method: 'POST',
          json: {
            organizationName,
            quoteTitle,
            conversation: next.map((t) => ({ role: t.role, content: t.content })),
            currentDocument,
          },
        },
      );
      setTurns([...next, { role: 'assistant', content: data.reply, draft: data.draft }]);
      // Straight into the quote. No button, because copying the SOW
      // across by hand is the step this whole feature removes.
      if (data.draft) onApply(data.draft);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Claude did not answer. Try again.');
      // The user's message stays on screen — retyping a paragraph of
      // scope because a request failed is the worst possible outcome.
      setTurns(next);
    } finally {
      setBusy(false);
    }
  }

  const latestDraftIndex = turns.reduce(
    (found, t, i) => (t.draft ? i : found),
    -1,
  );

  return (
    <div className="space-y-3">
      {turns.length === 0 ? (
        <div className="rounded-[var(--radius-button)] border border-dashed border-[var(--color-border)] bg-white p-5">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-t3-purple)]" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-[var(--color-header)]">
                Describe the engagement and I will scope it with you
              </p>
              <p className="mt-1 text-sm text-[var(--color-muted)]">
                Tell me what the client has, what is wrong, and roughly what you think it takes.
                I will ask about the rest and write the scope of work, the hours by area and the
                delivery plan into the quote as we go. The rate and the price stay yours.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {OPENERS.map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => void send(o)}
                    className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-xs text-[var(--color-body)] hover:border-[var(--color-t3-blue)]"
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-h-[32rem] space-y-3 overflow-y-auto rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-4">
          {turns.map((turn, index) => (
            <div key={index}>
              <div
                className={
                  turn.role === 'user'
                    ? 'ml-auto max-w-[85%] rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-3 py-2 text-sm text-white'
                    : 'max-w-[92%] rounded-[var(--radius-button)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-body)]'
                }
              >
                <p className="whitespace-pre-wrap">{turn.content}</p>
              </div>

              {turn.draft && (
                <div className="mt-2 flex flex-wrap items-center gap-3 rounded-[var(--radius-button)] border border-[var(--color-t3-purple)]/30 bg-purple-50/50 px-3 py-2">
                  <span className="text-sm text-[var(--color-header)]">
                    {index === latestDraftIndex
                      ? 'Written into the scope of work - ' + draftSummary(turn.draft)
                      : 'Earlier version - ' + draftSummary(turn.draft)}
                  </span>
                  {index !== latestDraftIndex && (
                    <Button variant="outline" onClick={() => onApply(turn.draft!)}>
                      Go back to this
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
          {busy && (
            <p className="text-sm text-[var(--color-muted)]">Thinking…</p>
          )}
          <div ref={endRef} />
        </div>
      )}

      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

      <div className="flex items-end gap-2">
        <textarea
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter breaks the line. Scoping notes run
            // to paragraphs, so the line break has to stay reachable.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send(input);
            }
          }}
          className={inputClass}
          placeholder={
            turns.length === 0
              ? 'Describe the engagement…'
              : 'Answer, or tell me what to change…'
          }
          disabled={busy}
        />
        <Button onClick={() => void send(input)} disabled={busy || !input.trim()}>
          <Send className="h-4 w-4" aria-hidden="true" />
          Send
        </Button>
      </div>
    </div>
  );
}
